import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { CheckCircle2, PackageCheck, Loader2, Truck, AlertTriangle, ScanBarcode, Sparkles } from "lucide-react";
import BarcodeScannerDialog from "./BarcodeScannerDialog";

const lineStatusBadge = (status) => {
  if (status === "received") return "bg-emerald-100 text-emerald-800";
  if (status === "over") return "bg-amber-100 text-amber-900";
  if (status === "partial") return "bg-blue-100 text-blue-800";
  if (status === "extra") return "bg-purple-100 text-purple-800";
  return "bg-slate-100 text-slate-700";
};

export default function ReceiveDeliveryDialog({ open, onOpenChange, project, onSuccess }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sku, setSku] = useState("");
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [flashId, setFlashId] = useState(null);
  const skuInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expected-deliveries", {
        params: { project_id: project.id, only_open: true },
      });
      setDeliveries(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && project) {
      setSku("");
      setQty("");
      load();
      setTimeout(() => skuInputRef.current?.focus(), 100);
    }
  }, [open, project?.id]);

  const normalizedSku = sku.trim().toUpperCase();

  // Client-side match preview so user sees what will happen
  const match = useMemo(() => {
    if (!normalizedSku) return null;
    for (const d of deliveries) {
      for (const l of d.lines) {
        if (l.unexpected) continue;
        if (l.sku.toUpperCase() !== normalizedSku) continue;
        if (l.status === "received" || l.status === "over") continue;
        return { ...l, delivery_id: d.id, reference: d.reference };
      }
    }
    return null;
  }, [normalizedSku, deliveries]);

  const flash = (lineId) => {
    setFlashId(lineId);
    setTimeout(() => setFlashId(null), 1200);
  };

  const doReceive = async (scannedSku, options = {}) => {
    const rawSku = String(scannedSku || sku).trim().toUpperCase();
    if (!rawSku) {
      toast.error("Enter or scan a SKU");
      return;
    }
    const n = Number(qty) > 0 ? Number(qty) : (options.autoQty || 1);
    setBusy(true);
    try {
      const { data } = await api.post("/expected-deliveries/scan", {
        project_id: project.id,
        sku: rawSku,
        quantity: n,
      });
      if (data.was_unexpected) {
        toast.warning(
          `Flagged ${rawSku} as an unexpected extra (${n} received) — please verify`,
          { icon: <Sparkles size={16} /> }
        );
      } else {
        // Find the matched line for a nicer toast
        const line = data.delivery.lines.find((l) => l.id === data.matched_line_id);
        const remainingText = line ? ` · ${line.remaining} still expected` : "";
        toast.success(`Received ${n} × ${line?.item_name || rawSku}${remainingText}`);
      }
      if (data.matched_line_id) flash(data.matched_line_id);
      setSku("");
      setQty("");
      skuInputRef.current?.focus();
      await load();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record scan");
    } finally {
      setBusy(false);
    }
  };

  const submitForm = (e) => {
    e?.preventDefault?.();
    doReceive();
  };

  const handleScanned = (code) => {
    // In bulk mode we keep the scanner open. Parent still fires the receive.
    setSku(code);
    setTimeout(() => doReceive(code), 30);
  };

  // Show a hint about what will happen when the user submits
  const previewHint = () => {
    if (!normalizedSku) return null;
    if (match) {
      const willBe = Math.min(
        match.expected_quantity,
        (match.received_quantity || 0) + (Number(qty) || 1)
      );
      return (
        <div className="mt-1 text-xs text-slate-700">
          Matches <span className="font-semibold text-slate-900">{match.item_name}</span>
          {" "}· current <span className="font-mono">{match.received_quantity}</span>
          {" "}/ <span className="font-mono">{match.expected_quantity}</span>
          {" "}→ will be <span className="font-mono font-semibold text-blue-800">{willBe}</span>
        </div>
      );
    }
    return (
      <div className="mt-1 flex items-center gap-1 text-xs text-amber-800">
        <AlertTriangle size={12} /> Not on the expected list — will be added and flagged for verification
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl rounded-md" data-testid="receive-delivery-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="grid place-items-center h-10 w-10 rounded-sm bg-blue-100 text-blue-800">
                <Truck size={20} />
              </div>
              <div>
                <DialogTitle className="font-display text-2xl font-black tracking-tight">
                  Receive Delivery on Site
                </DialogTitle>
                <DialogDescription className="text-slate-600 mt-1">
                  Scan or type the SKU. Each scan checks off one from the expected list for{" "}
                  <span className="font-semibold text-slate-900">{project?.name}</span>.
                  Unknown SKUs are added and flagged.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {loading ? (
            <div className="py-10 text-center font-mono text-sm text-slate-500">Loading expected list…</div>
          ) : (
            <div className="space-y-4">
              <form onSubmit={submitForm} className="bg-slate-50 border border-slate-200 rounded-md p-4 grid grid-cols-12 gap-3 items-end">
                <div className="col-span-12 md:col-span-6">
                  <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">SKU Code</Label>
                  <div className="mt-1.5 flex gap-2">
                    <Input
                      ref={skuInputRef}
                      value={sku}
                      onChange={(e) => setSku(e.target.value)}
                      placeholder="Type or scan the SKU"
                      data-testid="receive-sku-input"
                      className="rounded-sm font-mono uppercase h-11"
                    />
                    <Button
                      type="button"
                      onClick={() => setScannerOpen(true)}
                      data-testid="receive-scan-btn"
                      className="rounded-sm h-11 bg-slate-900 hover:bg-slate-800 text-white shrink-0"
                    >
                      <ScanBarcode size={18} className="mr-1.5" />
                      Scan
                    </Button>
                  </div>
                  {previewHint()}
                </div>
                <div className="col-span-6 md:col-span-3">
                  <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">
                    Qty <span className="text-slate-400 font-normal normal-case">(default 1)</span>
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    placeholder="1"
                    data-testid="receive-qty-input"
                    className="mt-1.5 rounded-sm font-mono h-11"
                  />
                </div>
                <div className="col-span-6 md:col-span-3">
                  <Button
                    type="submit"
                    disabled={busy || !normalizedSku}
                    data-testid="receive-submit-btn"
                    className="w-full h-11 rounded-sm bg-blue-800 hover:bg-blue-900 text-white font-semibold"
                  >
                    {busy ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <PackageCheck size={16} className="mr-1.5" />}
                    Check off
                  </Button>
                </div>
              </form>

              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                  Expected on this project
                </div>
                {deliveries.length === 0 ? (
                  <div className="py-8 text-center border border-slate-200 rounded-md">
                    <PackageCheck size={36} className="mx-auto text-emerald-600" />
                    <div className="mt-2 font-display text-lg font-bold text-slate-900">
                      Nothing expected right now
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                      You can still scan — anything unrecognised will be logged as a walk-in and flagged.
                    </p>
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-md divide-y divide-slate-200 max-h-[40vh] overflow-y-auto">
                    {deliveries.map((d) => (
                      <div key={d.id} data-testid={`expected-delivery-${d.id}`}>
                        <div className="bg-slate-50 px-4 py-2 flex items-center justify-between">
                          <div className="text-xs font-mono text-slate-700 font-semibold">
                            {d.reference || "no reference"}
                          </div>
                          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${
                            d.status === "complete" ? "bg-emerald-100 text-emerald-800" :
                            d.status === "partial" ? "bg-blue-100 text-blue-800" :
                            "bg-slate-100 text-slate-700"
                          }`}>{d.status}</span>
                        </div>
                        {d.lines.map((l) => {
                          const flashing = flashId === l.id;
                          return (
                            <button
                              key={l.id}
                              type="button"
                              onClick={() => {
                                setSku(l.sku);
                                setQty("");
                                skuInputRef.current?.focus();
                              }}
                              className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors ${flashing ? "bg-emerald-50" : ""}`}
                              data-testid={`expected-line-btn-${l.id}`}
                            >
                              <div className="w-5 shrink-0">
                                {l.status === "received" || l.status === "over" || l.status === "extra"
                                  ? <CheckCircle2 size={18} className={l.status === "extra" ? "text-purple-600" : "text-emerald-600"} />
                                  : <div className="h-4 w-4 border-2 border-slate-300 rounded-sm" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-sm font-semibold text-slate-900">{l.sku}</span>
                                  <span className="text-sm text-slate-700 truncate">{l.item_name}</span>
                                  {l.unexpected && (
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-purple-100 text-purple-800">
                                      <AlertTriangle size={10} /> Unexpected
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500 font-mono mt-0.5">
                                  {l.unexpected
                                    ? <>received {l.received_quantity} · not on original PO</>
                                    : <>{l.received_quantity} / {l.expected_quantity} received{l.remaining > 0 ? ` · ${l.remaining} remaining` : ""}</>
                                  }
                                </div>
                              </div>
                              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${lineStatusBadge(l.status)}`}>
                                {l.status}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BarcodeScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onDetected={handleScanned}
        continuous={true}
        title="Scan delivery barcode"
      />
    </>
  );
}
